/**
 * Fixture-only Services backend for the isolated Preview package.
 *
 * It never imports Supabase and deliberately remains process-local: Preview is
 * for rehearsing the complete request UX, not for pretending that a customer
 * request reached production operations.
 */
import {
  canOperatorTransitionServiceRequest,
  canRequesterAcceptServiceQuote,
  canRequesterCancelServiceRequest,
  type ServiceRequest,
  type ServiceRequestInput,
} from '../lib/services/services-catalog'

const requestsById = new Map<string, ServiceRequest>()
const requestIdByIdempotencyKey = new Map<string, string>()
let sequence = 0

function cloneRequest(request: ServiceRequest): ServiceRequest {
  return {
    ...request,
    location: { ...request.location },
    destination: request.destination ? { ...request.destination } : null,
    quote: request.quote ? { ...request.quote } : null,
    events: request.events.map((event) => ({ ...event })),
  }
}

export async function previewCreateServiceRequest(
  input: ServiceRequestInput,
): Promise<ServiceRequest> {
  const existingId = requestIdByIdempotencyKey.get(input.idempotencyKey)
  if (existingId) {
    const existing = requestsById.get(existingId)
    if (existing) return cloneRequest(existing)
  }

  const now = new Date().toISOString()
  const id = `service_preview_${++sequence}`
  const request: ServiceRequest = {
    id,
    category: input.category,
    need: input.need,
    location: { ...input.location },
    destination: input.destination ? { ...input.destination } : null,
    timing: input.timing,
    scheduledDate: input.scheduledDate,
    arrivalWindow: input.arrivalWindow,
    details: input.details,
    status: 'submitted',
    createdAt: now,
    updatedAt: now,
    canCancel: canRequesterCancelServiceRequest('submitted'),
    canAcceptQuote: false,
    quote: null,
    events: [{ id: `${id}_submitted`, kind: 'submitted', createdAt: now }],
  }
  requestsById.set(id, request)
  requestIdByIdempotencyKey.set(input.idempotencyKey, id)
  return cloneRequest(request)
}

export async function previewListServiceRequests(): Promise<ServiceRequest[]> {
  return [...requestsById.values()]
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
    .map(cloneRequest)
}

export async function previewGetServiceRequest(id: string): Promise<ServiceRequest | null> {
  const request = requestsById.get(id)
  return request ? cloneRequest(request) : null
}

export async function previewCancelServiceRequest(id: string): Promise<ServiceRequest> {
  const request = requestsById.get(id)
  if (!request || !canRequesterCancelServiceRequest(request.status)) {
    throw Object.assign(new Error('Service request cannot be cancelled'), {
      code: 'SERVICES_CANNOT_CANCEL',
    })
  }
  const now = new Date().toISOString()
  request.status = 'cancelled'
  request.updatedAt = now
  request.canCancel = false
  request.events = [...request.events, { id: `${id}_cancelled`, kind: 'cancelled', createdAt: now }]
  return cloneRequest(request)
}

/** Test-only operation rehearsal; it is intentionally never exposed in the Preview UI. */
export async function previewPublishQuoteForTest(id: string): Promise<ServiceRequest> {
  const request = requestsById.get(id)
  if (
    !request ||
    !canOperatorTransitionServiceRequest(request.status, 'matching') ||
    !canOperatorTransitionServiceRequest('matching', 'quoted')
  ) {
    throw new Error('Service request cannot receive a quote')
  }

  const matchingAt = new Date().toISOString()
  request.status = 'matching'
  request.updatedAt = matchingAt
  request.canCancel = canRequesterCancelServiceRequest(request.status)
  request.events = [
    ...request.events,
    { id: `${id}_matching`, kind: 'matching', createdAt: matchingAt },
  ]

  const quotedAt = new Date().toISOString()
  request.status = 'quoted'
  request.updatedAt = quotedAt
  request.canCancel = canRequesterCancelServiceRequest(request.status)
  request.quote = {
    amountCents: 12_500,
    currency: 'USD',
    summary: 'Equipo, traslado y coordinación según la solicitud recibida.',
    createdAt: quotedAt,
    acceptedAt: null,
  }
  request.canAcceptQuote = canRequesterAcceptServiceQuote(request.status, request.quote)
  request.events = [...request.events, { id: `${id}_quoted`, kind: 'quoted', createdAt: quotedAt }]
  return cloneRequest(request)
}

export async function previewAcceptServiceQuote(id: string): Promise<ServiceRequest> {
  const request = requestsById.get(id)
  const quote = request?.quote
  if (!request || !quote || !canRequesterAcceptServiceQuote(request.status, quote)) {
    throw Object.assign(new Error('Service quote cannot be accepted'), {
      code: 'SERVICES_CANNOT_ACCEPT_QUOTE',
    })
  }
  const now = new Date().toISOString()
  request.status = 'confirmed'
  request.updatedAt = now
  request.canCancel = false
  request.canAcceptQuote = false
  request.quote = { ...quote, acceptedAt: now }
  request.events = [...request.events, { id: `${id}_confirmed`, kind: 'confirmed', createdAt: now }]
  return cloneRequest(request)
}
